import TabContext from "@mui/lab/TabContext";
import TabList from "@mui/lab/TabList";
import { Box, Grid, Typography } from "@mui/material";
import Tab from "@mui/material/Tab";
import { useState } from "react";
import useColors from "../../../api/hooks/useColors";
import AppPage from "../../../components/structure/AppPage";
import SvgToIcon from "../../../icons/SvgToIcon";
import { icons } from "../../stack/environment/icons/iconExport";
import BuildWorkflowModal from "./components/BuildWorkflowModal";
import FirstWorkflowMessage from "./components/FirstWorkflowMessage";
import WorkflowCardCustom from "./components/WorkflowCard/WorkflowCardCustom";
import WorkflowCardTemplate from "./components/WorkflowCard/WorkflowCardTemplate";
import useWorkflows from "./hooks/useWorkflows";

const WorkflowTabs = {
  MyWorkflows: "My Workflows",
  Templates: "Templates",
  // History: "History",
};

const WorkflowsView = () => {
  const colors = useColors();
  const [activeTab, setActiveTab] = useState("MyWorkflows");
  const [buildWorkflowModalOpen, setBuildWorkflowModalOpen] = useState(false);
  const [editingWorkflow, setEditingWorkflow] = useState(null);
  const { workflows, templates, addWorkflow, updateWorkflow } = useWorkflows();

  const handleChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleWorkflowCreated = (newWorkflow) => {
    if (editingWorkflow) {
      updateWorkflow(editingWorkflow.id, newWorkflow);
      setEditingWorkflow(null);
    } else {
      addWorkflow(newWorkflow);
    }
  };

  const handleEditWorkflow = (workflow) => {
    setEditingWorkflow(workflow);
    setBuildWorkflowModalOpen(true);
  };

  const handleCloseModal = () => {
    setBuildWorkflowModalOpen(false);
    setEditingWorkflow(null);
  };

  const getWorkflowsForTab = (tabName) => {
    switch (tabName) {
      case "MyWorkflows":
        return workflows; // workflows in use
      case "Templates":
        return templates; // available workflows
      case "History":
        return workflows; // all workflows
      default:
        return [];
    }
  };

  return (
    <AppPage title="Workflows">
      <Box
        onClick={() => setBuildWorkflowModalOpen(true)}
        sx={{
          display: "flex",
          flexDirection: "row",
          gap: 1,
          alignItems: "center",
          justifyContent: "flex-end",
          mt: -4,
          cursor: "pointer",
          "&:hover": {
            color: colors.primary_main,
          },
        }}
      >
        <SvgToIcon
          IconLogo={icons.BuildWorkflowIcon}
          width="24px"
          height="24px"
        />
        <Typography variant="overline">Build My Own Workflow</Typography>
      </Box>
      <Box sx={{ position: "relative" }}>
        <TabContext value={activeTab}>
          <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
            <TabList
              onChange={handleChange}
              sx={{
                "& .MuiTabs-indicator": {
                  backgroundColor: colors.primary_main,
                },
              }}
            >
              {Object.entries(WorkflowTabs).map(([title, label], index) => {
                return (
                  <Tab
                    key={index}
                    value={title}
                    style={{ fontWeight: "500" }}
                    label={label}
                    sx={{
                      textTransform: "none",
                    }}
                  />
                );
              })}
            </TabList>
          </Box>
        </TabContext>
        <Grid container gap={3} sx={{ mt: 3, paddingBottom: "20px" }}>
          {(() => {
            const workflowsToShow = getWorkflowsForTab(activeTab);

            if (activeTab === "MyWorkflows" && workflowsToShow.length === 0) {
              return (
                <Grid
                  item
                  xs={12}
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    height: "calc(100vh - 200px)",
                    minHeight: "400px",
                  }}
                >
                  <FirstWorkflowMessage
                    onStartWithTemplate={() => setActiveTab("Templates")}
                    onBuildFromScratch={() => setBuildWorkflowModalOpen(true)}
                  />
                </Grid>
              );
            }

            return workflowsToShow?.map((workflow) =>
              activeTab === "Templates" ? (
                <WorkflowCardTemplate key={workflow.id} data={workflow} />
              ) : (
                <WorkflowCardCustom
                  key={workflow.id}
                  data={workflow}
                  onEdit={handleEditWorkflow}
                />
              )
            );
          })()}
        </Grid>
      </Box>
      <BuildWorkflowModal
        open={buildWorkflowModalOpen}
        onClose={handleCloseModal}
        onWorkflowCreated={handleWorkflowCreated}
        editingWorkflow={editingWorkflow}
      />
    </AppPage>
  );
};

export default WorkflowsView;
